import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
	DataGrid,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
} from "@material-ui/data-grid";
import { Typography, Button, Tooltip } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

function CustomToolbar() {
	return (
		<GridToolbarContainer>
			<GridToolbarColumnsButton />
			<GridToolbarFilterButton />
		</GridToolbarContainer>
	);
}

export default function Files() {
	const { enqueueSnackbar } = useSnackbar();
	const [files, setFiles] = useState([]);
	const [selectionModel, setSelectionModel] = useState([]);
	const [sortModel, setSortModel] = useState([
		{ field: "lastModifiedAt", sort: "desc" },
	]);

	useEffect(() => {
		const fetchFiles = async () => {
			const res = await fetch(`${process.env.REACT_APP_API_URL}/files`);
			const data = await res.json();
			return data;
		};

		const getFiles = async () => {
			let filesFromServer = await fetchFiles();

			filesFromServer = filesFromServer.data.map((file) => {
				return {
					...file,
					uploadedAt: dayjs(file.uploadedAt).format("YYYY-MM-DD HH:mm:ss"),
					lastModifiedAt: dayjs(file.lastModifiedAt).format(
						"YYYY-MM-DD HH:mm:ss"
					),
				};
			});

			setFiles(filesFromServer);
		};
		getFiles();
	}, []);

	const deleteFromAPI = async (id) => {
		try {
			await fetch(`${process.env.REACT_APP_API_URL}/files/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
		} catch (e) {
			console.log(e);
		}
	};

	const onClickDeleteAll = async () => {
		await selectionModel.map((id) => deleteFromAPI(id));

		//Remove selectionModeled items from the rows state
		let newFiles = [...files];
		for (let i = 0; i < selectionModel.length; i++) {
			newFiles = newFiles.filter((file) => file._id !== selectionModel[i]);
		}
		await setFiles(newFiles);

		//Clear selection
		setSelectionModel([]);
	};

	const columns = useMemo(
		() => [
			{ field: "_id", headerName: "ID", hide: true },
			{
				field: "thumbnailName",
				headerName: "Thumbnail",
				width: 135,
				renderCell: (params) => (
					<Link
						to={`/${process.env.REACT_APP_ADMIN_URL}/files/${params.row._id}`}
					>
						{params.value ? (
							<div
								style={{
									borderRadius: "5px",
									height: "100px",
									width: "100px",
									background: `url('${process.env.REACT_APP_FILE_URL}/${params.value}')`,
									backgroundSize: "cover",
									backgroundPosition: "center center",
								}}
							/>
						) : (
							<div
								style={{
									borderRadius: "5px",
									border: "2px dashed #ccc",
									height: "100px",
									width: "100px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<InsertDriveFileIcon
									fontSize="large"
									style={{ color: "#555" }}
								/>
							</div>
						)}
					</Link>
				),
			},
			{
				field: "fileName",
				headerName: "File Name",
				flex: 2,
				renderCell: (params) => (
					<Tooltip
						title={`Copy URL for ${params.value}`}
						placement="bottom-start"
					>
						<Typography
							style={{ cursor: "pointer" }}
							color="primary"
							onClick={(e) => {
								navigator.clipboard.writeText(
									`${process.env.REACT_APP_FILE_URL}/${params.value}`
								);
								enqueueSnackbar(`URL copied to clipboard`, {
									variant: "success",
								});
							}}
						>
							{params.value}
						</Typography>
					</Tooltip>
				),
			},
			{
				field: "uploadedAt",
				headerName: "Uploaded At",
				flex: 1,
			},
			{
				field: "lastModifiedAt",
				headerName: "Last Modified",
				flex: 1,
			},
		],
		[enqueueSnackbar]
	);

	const useStyles = makeStyles({
		datagrid: {
			backgroundColor: "white",
			//Remove outlines when clicking a cell in the datagrid
			"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
				outline: 0,
			},
		},
	});

	const classes = useStyles();

	return (
		<>
			<div
				style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}
			>
				<Typography component="h1" variant="h4">
					Files
				</Typography>
				<Button
					style={{ marginLeft: "1rem" }}
					color="primary"
					variant="contained"
					component={Link}
					to={`/${process.env.REACT_APP_ADMIN_URL}/files/add`}
				>
					Add New
				</Button>
				{selectionModel && selectionModel.length > 0 && (
					<Button
						startIcon={<DeleteIcon />}
						style={{ marginLeft: "1rem" }}
						color="secondary"
						variant="contained"
						onClick={onClickDeleteAll}
					>
						Delete
					</Button>
				)}
			</div>
			<DataGrid
				className={classes.datagrid}
				components={{
					Toolbar: CustomToolbar,
				}}
				getRowId={(row) => row._id}
				autoHeight
				rowHeight="110"
				rows={files}
				columns={columns}
				pageSize={25}
				checkboxSelection
				disableSelectionOnClick
				sortModel={sortModel}
				onSortModelChange={(model) => setSortModel(model)}
				selectionModel={selectionModel}
				onSelectionModelChange={(newSelection) => {
					setSelectionModel(newSelection);
				}}
			/>
		</>
	);
}
